import { useMemo } from "react";
import { useGetItemsQuery } from "../../features/items/itemApi";
import { useGetLinesQuery } from "../../features/lines/lineApi";
import { useGetMachinesQuery } from "../../features/machines/machineApi";
import { useGetOemsQuery } from "../../features/oems/oemApi";
import { useGetOperationsQuery } from "../../features/operations/operationApi";
import { useGetProcessesQuery } from "../../features/processes/processApi";
import { useGetRawMaterialsQuery } from "../../features/rawMaterials/rawMaterialApi";
import { useGetRolesQuery } from "../../features/roles/roleApi";
import { useGetSettingsQuery } from "../../features/settings/settingsApi";
import { useGetShiftsQuery } from "../../features/shifts/shiftApi";
import { useGetRelationTypesQuery } from "../../features/relationTypes/relationTypeApi";
import { buildNameCodeIdLabel } from "./masterMetadata";

const hasSource = (fields, source) =>
  fields.some((field) => field.optionSource === source);

const getQueryArgs = (plantId) => ({
  page: 1,
  limit: 1000,
  plant_id: plantId,
});

const buildOptions = (rows, mapper) =>
  (rows || [])
    .map(mapper)
    .filter((option) => option?.value)
    .map((option) => ({
      ...option,
      value: String(option.value),
    }));

const useResolvedFilterFields = (fields = [], plantId = "") => {
  const canLoad = Boolean(plantId);
  const queryArgs = useMemo(() => getQueryArgs(plantId), [plantId]);

  const needsRawMaterials = canLoad && hasSource(fields, "rawMaterials");
  const needsItems = canLoad && hasSource(fields, "items");
  const needsOems = canLoad && hasSource(fields, "oems");
  const needsMachines = canLoad && hasSource(fields, "machines");
  const needsLines = canLoad && hasSource(fields, "lines");
  const needsOperations = canLoad && hasSource(fields, "operations");
  const needsProcesses = canLoad && hasSource(fields, "processes");
  const needsRoles = canLoad && hasSource(fields, "roles");
  const needsShifts = canLoad && hasSource(fields, "shifts");
  const needsSettings = canLoad && hasSource(fields, "settings");
  const needsRelationTypes = canLoad && hasSource(fields, "relationTypes");

  const rawMaterialQuery = useGetRawMaterialsQuery(queryArgs, { skip: !needsRawMaterials });
  const itemQuery = useGetItemsQuery(queryArgs, { skip: !needsItems });
  const oemQuery = useGetOemsQuery(queryArgs, { skip: !needsOems });
  const machineQuery = useGetMachinesQuery(queryArgs, { skip: !needsMachines });
  const lineQuery = useGetLinesQuery(queryArgs, { skip: !needsLines });
  const operationQuery = useGetOperationsQuery(queryArgs, { skip: !needsOperations });
  const processQuery = useGetProcessesQuery(queryArgs, { skip: !needsProcesses });
  const roleQuery = useGetRolesQuery(queryArgs, { skip: !needsRoles });
  const shiftQuery = useGetShiftsQuery(queryArgs, { skip: !needsShifts });
  const settingsQuery = useGetSettingsQuery(queryArgs, { skip: !needsSettings });
  const relationTypeQuery = useGetRelationTypesQuery(queryArgs, { skip: !needsRelationTypes });

  const sourceConfig = useMemo(
    () => ({
      relationTypes: {
        isFetching: relationTypeQuery.isFetching,
        options: buildOptions(relationTypeQuery.data?.data, (relation) => ({
          value: relation?.relation_id,
          label: buildNameCodeIdLabel({
            name: relation?.relation_type,
            id: relation?.relation_id,
          }),
        })),
      },
      rawMaterials: {
        isFetching: rawMaterialQuery.isFetching,
        options: buildOptions(rawMaterialQuery.data?.data, (material) => ({
          value: material?.raw_material_id,
          label: buildNameCodeIdLabel({
            name: material?.raw_material_name,
            code: material?.raw_material_code,
            id: material?.raw_material_id,
          }),
        })),
      },
      items: {
        isFetching: itemQuery.isFetching,
        options: buildOptions(itemQuery.data?.data, (item) => ({
          value: item?.item_id,
          label: buildNameCodeIdLabel({
            name: item?.item_name,
            code: item?.item_code,
            id: item?.item_id,
          }),
        })),
      },
      oems: {
        isFetching: oemQuery.isFetching,
        options: buildOptions(oemQuery.data?.data, (oem) => ({
          value: oem?.oem_id,
          label: buildNameCodeIdLabel({
            name: oem?.oem_name,
            code: oem?.oem_code,
            id: oem?.oem_id,
          }),
        })),
      },
      machines: {
        isFetching: machineQuery.isFetching,
        options: buildOptions(machineQuery.data?.data, (machine) => ({
          value: machine?.machine_id,
          label: buildNameCodeIdLabel({
            name: machine?.machine_name,
            id: machine?.machine_id,
          }),
        })),
      },
      lines: {
        isFetching: lineQuery.isFetching,
        options: buildOptions(lineQuery.data?.data, (line) => ({
          value: line?.line_id,
          label: buildNameCodeIdLabel({
            name: line?.line_name,
            code: line?.line_code,
            id: line?.line_id,
          }),
        })),
      },
      operations: {
        isFetching: operationQuery.isFetching,
        options: buildOptions(operationQuery.data?.data, (operation) => ({
          value: operation?.operation_id,
          label: buildNameCodeIdLabel({
            name: operation?.operation_name,
            code: operation?.operation_code,
            id: operation?.operation_id,
          }),
        })),
      },
      processes: {
        isFetching: processQuery.isFetching,
        options: buildOptions(processQuery.data?.data, (process) => ({
          value: process?.process_id,
          label: buildNameCodeIdLabel({
            name: process?.process_name,
            code: process?.process_code,
            id: process?.process_id,
          }),
        })),
      },
      roles: {
        isFetching: roleQuery.isFetching,
        options: buildOptions(roleQuery.data?.data, (role) => ({
          value: role?.role_id,
          label: buildNameCodeIdLabel({
            name: role?.role_name,
            id: role?.role_id,
          }),
        })),
      },
      shifts: {
        isFetching: shiftQuery.isFetching,
        options: buildOptions(shiftQuery.data?.data, (shift) => ({
          value: shift?.shift_id,
          label: buildNameCodeIdLabel({
            name: shift?.shift_name,
            id: shift?.shift_id,
          }),
        })),
      },
      settings: {
        isFetching: settingsQuery.isFetching,
        options: buildOptions(settingsQuery.data?.data, (settings) => ({
          value: settings?.setting_id,
          label: buildNameCodeIdLabel({
            name: settings?.setting_id,
            id: settings?.setting_id,
          }),
        })),
      },
    }),
    [
      relationTypeQuery.data?.data,
      relationTypeQuery.isFetching,
      rawMaterialQuery.data?.data,
      rawMaterialQuery.isFetching,
      itemQuery.data?.data,
      itemQuery.isFetching,
      lineQuery.data?.data,
      lineQuery.isFetching,
      machineQuery.data?.data,
      machineQuery.isFetching,
      oemQuery.data?.data,
      oemQuery.isFetching,
      operationQuery.data?.data,
      operationQuery.isFetching,
      processQuery.data?.data,
      processQuery.isFetching,
      roleQuery.data?.data,
      roleQuery.isFetching,
      settingsQuery.data?.data,
      settingsQuery.isFetching,
      shiftQuery.data?.data,
      shiftQuery.isFetching,
    ]
  );

  return useMemo(
    () =>
      fields.map((field) => {
        if (!field.optionSource) return field;

        const config = sourceConfig[field.optionSource] || {};
        const isLoading = Boolean(config.isFetching);

        return {
          ...field,
          type: "select",
          options: config.options || [],
          placeholder: !plantId
            ? "Select plant first"
            : isLoading
            ? `Loading ${field.label.toLowerCase()}...`
            : field.placeholder || `All ${field.label}`,
          disabled:
            Boolean(field.disabled) ||
            !plantId ||
            (isLoading && (config.options || []).length === 0),
        };
      }),
    [fields, plantId, sourceConfig]
  );
};

export default useResolvedFilterFields;
